// Routes on a Connect path (step 9.25; docs/specs/CONNECT.md section 21; migration 20270219090000): after a step, the
// first of its routes whose condition holds decides where the firm goes. The condition grammar is the Quote evaluator's
// (supabase/functions/quote/lib/evaluate.ts Condition), and "Try it" decides with its very holds(), as Pricing does.
// The cleaning is the database's (crm__clean_routes) and engine/paths.py's clean_routes; the shared cases are in
// supabase/tests/fixtures/routes_clean_cases.json and condition_cases.json.
import { holds, type Answers, type ConditionOp } from "../../../../supabase/functions/quote/lib/evaluate";

export type { ConditionOp };
export { CONDITION_OPS } from "../../../../supabase/functions/quote/lib/evaluate";

/** The evaluator's Condition with its values as settings hold them (plain JSON), so it is one in every way. */
export type ConditionValue = string | number | boolean | (string | number)[];
export type Condition =
  | { q: string; op: ConditionOp; value?: ConditionValue }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

/** Where a route goes that is not a step: they said yes, lost (the loss route), ReConnect later, or a person picks. */
export const ROUTE_ENDS = ["end_yes", "end_lost", "reconnect", "person"] as const;
export type RouteEnd = (typeof ROUTE_ENDS)[number];
export type RouteOn = "done" | "timed_out";

export type Route = {
  from: string;
  on: RouteOn;
  when?: Condition;
  /** A later step of the path, or one of ROUTE_ENDS (and Quote again from Accept). */
  to: string;
  /** For ReConnect: days before it tries again (1 to 365; 90 when not set). */
  reapproach_days?: number;
}

/** The facts a condition may test (crm__fact_re; engine/paths.py FACT_RE). */
export const FACT_RE = /^(outcome\.(result|total|option|reason)|answer\.[a-z][a-z0-9_]{0,39}|firm\.(staff|sector|country|turnover)|campaign\.(id|module))$/;
export const MAX_ROUTES_FROM = 8;
const OPS: readonly string[] = ["eq", "ne", "gt", "gte", "lt", "lte", "in", "not_in", "prefix_in", "not_prefix_in", "present", "missing"];

const plain = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** crm__condition_problem: why a condition can't be used, or null. */
export function conditionProblem(c: unknown, depth = 0): string | null {
  if (depth > 6) return "the condition is nested too deeply";
  if (!plain(c)) return "a condition must be an object";
  if ("all" in c || "any" in c || "not" in c) {
    if (Object.keys(c).length !== 1) return "all, any and not stand alone in a condition";
    if ("not" in c) return conditionProblem(c.not, depth + 1);
    const parts = c.all ?? c.any;
    if (!Array.isArray(parts) || parts.length < 1 || parts.length > 20) return "all and any need 1 to 20 parts";
    for (const p of parts) {
      const e = conditionProblem(p, depth + 1);
      if (e) return e;
    }
    return null;
  }
  for (const k of Object.keys(c)) if (!["q", "op", "value"].includes(k)) return `${k.slice(0, 40)} is not part of a condition`;
  if (typeof c.q !== "string" || !FACT_RE.test(c.q)) return `${typeof c.q === "string" ? c.q.slice(0, 60) : "nothing"} is not a fact Ogma knows`;
  const op = c.op;
  if (typeof op !== "string" || !OPS.includes(op)) return `${typeof op === "string" ? op.slice(0, 40) : "nothing"} is not a test Ogma knows`;
  const v = c.value;
  if (op === "present" || op === "missing") return "value" in c ? `${op} takes no value` : null;
  if (["in", "not_in", "prefix_in", "not_prefix_in"].includes(op)) {
    return Array.isArray(v) && v.length >= 1 && v.length <= 200 && v.every((x) => typeof x === "string" || isNum(x)) ? null : `${op} needs a list of 1 to 200 values`;
  }
  if (["gt", "gte", "lt", "lte"].includes(op)) return isNum(v) ? null : `${op} needs a number`;
  return typeof v === "string" || typeof v === "boolean" || isNum(v) ? null : `${op} needs a value`;
}

/** crm__route_problem: why a route can't be used on a path whose steps in force are `blocks`, or null. */
export function routeProblem(r: unknown, blocks: readonly string[]): string | null {
  if (!plain(r)) return "a route must be an object";
  for (const k of Object.keys(r)) if (!["from", "on", "when", "to", "reapproach_days"].includes(k)) return `${k.slice(0, 40)} is not part of a route`;
  const from = typeof r.from === "string" ? r.from : null;
  if (from === null || !blocks.includes(from)) return `it starts from ${from ? from.slice(0, 40) : "nothing"}, which is not a step of this path`;
  if (r.on !== "done" && r.on !== "timed_out") return "on must be done or timed_out";
  const to = typeof r.to === "string" ? r.to : null;
  if (to === null || !((ROUTE_ENDS as readonly string[]).includes(to) || blocks.includes(to))) return `it goes to ${to ? to.slice(0, 40) : "nothing"}, which is not a step of this path`;
  if (blocks.includes(to) && blocks.indexOf(to) <= blocks.indexOf(from) && !(from === "accept" && to === "quote")) {
    return `it goes back from ${from} to ${to}: routes only go forward (except Accept back to Quote)`;
  }
  if ("reapproach_days" in r) {
    if (to !== "reconnect") return "reapproach_days is only for a route to ReConnect";
    const d = r.reapproach_days;
    if (!isNum(d) || !Number.isInteger(d) || d < 1 || d > 365) return "reapproach_days must be a whole number of days from 1 to 365";
  }
  return "when" in r ? conditionProblem(r.when) : null;
}

/** crm__clean_routes: the usable routes in order, at most 8 from each step. */
export function cleanRoutes(raw: unknown, blocks: readonly string[]): Route[] {
  const out: Route[] = [];
  if (!Array.isArray(raw)) return out;
  for (const r of raw) {
    if (routeProblem(r, blocks) !== null) continue;
    const route = r as Route;
    if (out.filter((o) => o.from === route.from).length >= MAX_ROUTES_FROM) continue;
    out.push(route);
  }
  return out;
}

/** Facts for a condition, keyed as the database builds them ({"firm.staff": 6, "outcome.result": "priced"}). */
export type Facts = Answers;

/**
 * "Try it": where a firm with these facts goes after `from` (crm__next_block, with the same holds as the quote
 * evaluator): the first route from that step whose condition holds, else the next step. `blocks` are the path's steps
 * in force; a timed-out try follows the timed_out routes.
 */
export function tryRoute(routes: readonly Route[], blocks: readonly string[], from: string, facts: Facts, on: RouteOn = "done"): { to: string; rule: number | null } {
  const at = blocks.indexOf(from);
  for (const [i, r] of routes.entries()) {
    if (r.from !== from || r.on !== on) continue;
    if (r.when && !holds(r.when, facts)) continue;
    return { to: r.to, rule: i };
  }
  return { to: blocks[at + 1] ?? "end", rule: null };
}

/** The facts a condition reads (for the route log and the words). */
export function conditionFacts(c: Condition): string[] {
  if ("all" in c) return [...new Set(c.all.flatMap(conditionFacts))];
  if ("any" in c) return [...new Set(c.any.flatMap(conditionFacts))];
  if ("not" in c) return conditionFacts(c.not);
  return [c.q];
}
